import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaHistory, FaCalendarAlt, FaDollarSign, FaCheckCircle, FaClock, FaTimes, FaSpinner, FaFilter, FaSort, FaCoins, FaGift } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/LoadingSpinner';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;
const PaymentHistoryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 1rem;
  font-family: 'Inter', sans-serif;
  animation: ${fadeIn} 220ms ease-out both;
  
  @media (max-width: 1024px) {
    padding: 0.9rem 0.9rem;
  }
  
  @media (max-width: 640px) {
    padding: 0.75rem 0.75rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 1.25rem;
  
  h1 {
    color: #2c3e50;
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
    font-weight: 700;
    font-family: 'Inter', sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
  
  p {
    color: #7f8c8d;
    font-size: 0.95rem;
    max-width: 500px;
    margin: 0 auto 0.75rem auto;
    font-family: 'Inter', sans-serif;
    line-height: 1.6;
  }
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ButtonSmall = styled.button`
  padding: 0.3rem 0.6rem;
  font-size: 0.75rem;
  border-radius: 9999px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  transition: all 160ms ease;
  cursor: pointer;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
`;

const ActiveFiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
`;

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: #f9fafb;
  border-bottom: 1px solid #f1f5f9;
`;

const StatsChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.6rem;
  border-radius: 9999px;
  border: 1px solid ${props => props.$border || '#d1d5db'};
  color: ${props => props.$colorText || '#374151'};
  background: ${props => props.$bg || '#ffffff'};
`;
const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-size: 0.85rem;
    color: #4b5563;
    font-weight: 500;
    white-space: nowrap;
  }
  
  select {
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.85rem;
    background: white;
    color: #374151;
    
    &:focus {
      outline: none;
      border-color: #22c55e;
      box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
    }
  }
`;

const TransactionsContainer = styled.div`
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  animation: ${slideUp} 220ms ease-out both;
`;

const TransactionHeader = styled.div`
  background: #ffffff;
  color: #0f172a;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border-bottom: 1px solid #eef2f7;
  position: sticky;
  top: 0;
  z-index: 1;
  
  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  svg {
    font-size: 1rem;
    color: #15803d;
  }
`;

const TransactionsList = styled.div`
  max-height: ${props => (props.$count === 0 ? '0px' : `${Math.min(props.$count * 72, 520)}px`)};
  overflow-y: auto;
  transition: max-height 220ms ease;
`;

const TabNav = styled.div`
  display: inline-flex;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  padding: 0.25rem;
  margin-bottom: 0.75rem;
  gap: 0.25rem;
`;

const TabButton = styled.button`
  padding: 0.4rem 0.9rem;
  border-radius: 9999px;
  border: none;
  background: ${props => (props.$active ? '#15803d' : 'transparent')};
  color: ${props => (props.$active ? '#ffffff' : '#0f172a')};
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => (props.$active ? '#15803d' : '#e5f5ec')};
  }
`;
const TransactionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0.8rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 160ms ease, transform 160ms ease;
  animation: ${slideUp} 220ms ease-out both;
  animation-delay: ${props => (props.$delay || 0)}ms;
  border-left: 3px solid ${props => {
    switch (props.$status) {
      case 'approved':
      case 'processed':
      case 'delivered':
        return '#16a34a';
      case 'pending':
      case 'processing':
        return '#f59e0b';
      case 'rejected':
      case 'cancelled':
        return '#ef4444';
      case 'expired':
        return '#6b7280';
      default:
        return '#9ca3af';
    }
  }};
  background: ${props =>
    props.$status === 'approved' || props.$status === 'processed' || props.$status === 'delivered'
      ? 'rgba(22,163,74,0.02)'
      : props.$status === 'pending' || props.$status === 'processing'
        ? 'rgba(245,158,11,0.02)'
        : props.$status === 'rejected' || props.$status === 'cancelled'
          ? 'rgba(239,68,68,0.02)'
          : 'transparent'};
  
  &:last-child { border-bottom: none; }
  &:hover { background-color: rgba(22, 163, 74, 0.05); transform: translateY(-1px); }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.6rem;
  }
`;

const TransactionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex: 1;
  
  .transaction-id {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1f2937;
    font-family: 'Inter', sans-serif;
  }
  
  .transaction-date {
    font-size: 0.78rem;
    color: #6b7280;
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }
  
  .bank-details {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.2rem;
  }
`;

const TransactionAmount = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.3rem;
  
  .amount {
    font-size: 1rem;
    font-weight: 700;
    color: #15803d;
    font-family: 'Inter', sans-serif;
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }
  
  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  
  ${props => {
    switch (props.status) {
      case 'approved':
        return `
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        `;
      case 'delivered':
        return `
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        `;
      case 'pending':
        return `
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #fde68a;
        `;
      case 'rejected':
        return `
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        `;
      case 'processed':
        return `
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
        `;
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 1.5rem;
  color: #6b7280;
  
  svg {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
    color: #374151;
  }
  
  p {
    font-size: 0.9rem;
    line-height: 1.6;
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem;
  
  button {
    padding: 0.5rem 1rem;
    border: 1px solid #d1d5db;
    background: white;
    color: #374151;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s ease;
    
    &:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #9ca3af;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    &.active {
      background: #22c55e;
      color: white;
      border-color: #22c55e;
    }
  }
  
  .page-info {
    font-size: 0.85rem;
    color: #6b7280;
  }
`;

const PaymentHistory = () => {
  const { user } = useAuth();
  const { socket } = useSocket();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('requestDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [totalTransactions, setTotalTransactions] = useState(0);

  const [redemptions, setRedemptions] = useState([]);
  const [redemptionsLoading, setRedemptionsLoading] = useState(true);
  const [redemptionsError, setRedemptionsError] = useState('');
  const [redemptionsPage, setRedemptionsPage] = useState(1);
  const [redemptionsPages, setRedemptionsPages] = useState(1);
  const [redemptionsStatusFilter, setRedemptionsStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showRedemptionFilters, setShowRedemptionFilters] = useState(false);
  
  const transactionStatusCounts = useMemo(() => {
    return transactions.reduce((acc, t) => {
      const s = t.status || 'unknown';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
  }, [transactions]);
  
  const redemptionStatusCounts = useMemo(() => {
    return redemptions.reduce((acc, r) => {
      const s = r.status || 'unknown';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
  }, [redemptions]);
  
  const statusTone = (status) => {
    switch (status) {
      case 'approved':
      case 'processed':
      case 'delivered':
        return { bg: '#dcfce7', border: '#bbf7d0', text: '#166534' };
      case 'pending':
      case 'processing':
        return { bg: '#fef3c7', border: '#fde68a', text: '#92400e' };
      case 'rejected':
      case 'cancelled':
        return { bg: '#fee2e2', border: '#fecaca', text: '#991b1b' };
      case 'expired':
        return { bg: '#e5e7eb', border: '#d1d5db', text: '#374151' };
      default:
        return { bg: '#f3f4f6', border: '#d1d5db', text: '#374151' };
    }
  };

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: currentPage,
        limit: 10,
        sortBy,
        sortOrder,
        ...(statusFilter !== 'all' && { status: statusFilter })
      };

      const response = await axios.get('/users/withdrawal-history', {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setTransactions(response.data.withdrawalHistory || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setTotalTransactions(response.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setError(err.response?.data?.message || 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortBy, sortOrder, statusFilter]);

  const fetchRedemptions = useCallback(async () => {
    try {
      setRedemptionsLoading(true);
      setRedemptionsError('');

      const params = {
        page: redemptionsPage,
        limit: 10,
        ...(redemptionsStatusFilter !== 'all' && { status: redemptionsStatusFilter })
      };

      const response = await axios.get('/users/rewards/history', {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setRedemptions(response.data.redemptions || []);
      setRedemptionsPages(response.data.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Error fetching redemptions history:', err);
      setRedemptionsError(err.response?.data?.message || 'Failed to load redemptions');
    } finally {
      setRedemptionsLoading(false);
    }
  }, [redemptionsPage, redemptionsStatusFilter]);

  useEffect(() => {
    fetchTransactions();
    fetchRedemptions();
  }, [currentPage, sortBy, sortOrder, statusFilter, redemptionsPage, redemptionsStatusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add window focus event listener to refresh data when user returns to page
  useEffect(() => {
    const handleWindowFocus = () => {
      // Refresh transactions when window gains focus (user returns to tab)
      fetchTransactions();
    };

    const handleVisibilityChange = () => {
      // Refresh transactions when page becomes visible
      if (!document.hidden) {
        fetchTransactions();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchTransactions]);

  // Socket.io listener for real-time point updates
  useEffect(() => {
    if (socket && user) {
      const handlePointsUpdate = (data) => {
        console.log('🔍 DEBUG: PaymentHistory - Received pointsUpdated event:', data);

        // Show toast notification based on the reason
        if (data.reason === 'withdrawal_approved') {
          toast.success(`Withdrawal approved! ${Math.abs(data.change)} points deducted.`);
        } else if (data.reason === 'withdrawal_rejected') {
          toast.info(`Withdrawal rejected. ${data.change} points restored to your account.`);
        }

        // Refresh transaction history to show updated status
        fetchTransactions();
        fetchRedemptions();
      };

      socket.on('pointsUpdated', handlePointsUpdate);

      return () => {
        socket.off('pointsUpdated', handlePointsUpdate);
      };
    }
  }, [socket, user, fetchTransactions]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <FaCheckCircle />;
      case 'pending':
        return <FaClock />;
      case 'rejected':
        return <FaTimes />;
      case 'processed':
        return <FaCheckCircle />;
      case 'delivered':
        return <FaCheckCircle />;
      case 'cancelled':
        return <FaTimes />;
      case 'processing':
        return <FaClock />;
      default:
        return <FaClock />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPoints = (amount) => {
    return `${amount} points`;
  };

  if (loading && transactions.length === 0) {
    return (
      <PaymentHistoryContainer>
        <LoadingSpinner />
      </PaymentHistoryContainer>
    );
  }

  return (
    <PaymentHistoryContainer>
      <PageHeader>
        <h1>
          <FaHistory />
          Payment History
        </h1>
        <p>
          Track all your withdrawal requests and their current status.
          View transaction details, amounts, and processing updates.
        </p>
      </PageHeader>

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      <TabNav>
        <TabButton
          $active={true}
          as="span"
          style={{ pointerEvents: 'none', opacity: 1, background: 'transparent', color: '#0f172a' }}
        >
          Sections
        </TabButton>
        <TabButton
          $active={false}
          as="span"
          style={{ pointerEvents: 'none', opacity: 0 }}
        />
      </TabNav>

      <TransactionsContainer>
        <TransactionHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FaGift />
            <h3>Gift Card Redemptions</h3>
          </div>
          <HeaderActions>
            <ButtonSmall onClick={() => setShowRedemptionFilters(v => !v)}>
              Filters
            </ButtonSmall>
          </HeaderActions>
        </TransactionHeader>

        {showRedemptionFilters && (
          <FilterSection>
            <FilterGroup>
              <FaFilter />
              <label>Status:</label>
              <select
                value={redemptionsStatusFilter}
                onChange={(e) => {
                  setRedemptionsStatusFilter(e.target.value);
                  setRedemptionsPage(1);
                }}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="delivered">Approved</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
            </FilterGroup>
          </FilterSection>
        )}

        {redemptionsLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <FaSpinner className="fa-spin" style={{ fontSize: '2rem', color: '#22c55e' }} />
            <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading redemptions...</p>
          </div>
        ) : redemptionsError ? (
          <ErrorMessage>{redemptionsError}</ErrorMessage>
        ) : redemptions.length === 0 ? (
          <EmptyState>
            <FaHistory />
            <h3>No Redemptions</h3>
            <p>
              You have not redeemed any rewards yet.
            </p>
          </EmptyState>
        ) : (
          <TransactionsList $count={redemptions.length}>
            {redemptions.map((r, index) => (
              <TransactionItem key={r.id} $status={r.status} $delay={index * 40}>
                <TransactionInfo>
                  <div className="transaction-id">
                    {r.Reward?.name || 'Reward'}
                  </div>
                  <div className="transaction-date">
                    <FaCalendarAlt />
                    Redeemed: {formatDate(r.createdAt)}
                    {r.deliveredAt && (
                      <span style={{ marginLeft: '1rem' }}>
                        Delivered: {formatDate(r.deliveredAt)}
                      </span>
                    )}
                  </div>
                  {r.status === 'delivered' && (r.voucherCode || (r.voucher && r.voucher.code)) && (
                    <div style={{
                      fontSize: '0.78rem',
                      color: '#1f2937',
                      marginTop: '0.35rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ fontWeight: 600, color: '#15803d' }}>Voucher Code:</span>
                      <code style={{
                        background: '#f3f4f6',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        padding: '0.2rem 0.4rem',
                        color: '#111827'
                      }}>{r.voucherCode || (r.voucher && r.voucher.code)}</code>
                      <button
                        onClick={() => {
                          const codeToCopy = r.voucherCode || (r.voucher && r.voucher.code);
                          if (navigator?.clipboard?.writeText) {
                            navigator.clipboard.writeText(codeToCopy)
                              .then(() => toast.success('Voucher code copied to clipboard'))
                              .catch(() => toast.error('Failed to copy code'));
                          } else {
                            toast.info('Copy not supported in this browser');
                          }
                        }}
                        style={{
                          padding: '0.2rem 0.5rem',
                          border: '1px solid #d1d5db',
                          background: '#ffffff',
                          color: '#374151',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                        aria-label="Copy voucher code"
                      >
                        Copy
                      </button>
                    </div>
                  )}
                </TransactionInfo>
                <TransactionAmount>
                  <div className="amount">
                    <FaCoins />
                    {formatPoints(r.pointsSpent)}
                  </div>
                  <StatusBadge status={r.status}>
                    {getStatusIcon(r.status)}
                    {r.status === 'delivered' ? 'approved' : r.status}
                  </StatusBadge>
                </TransactionAmount>
              </TransactionItem>
            ))}
          </TransactionsList>
        )}

        {redemptionsPages > 1 && (
          <PaginationContainer>
            <button
              onClick={() => setRedemptionsPage(prev => Math.max(prev - 1, 1))}
              disabled={redemptionsPage === 1}
            >
              Previous
            </button>
            <div className="page-info">
              Page {redemptionsPage} of {redemptionsPages}
            </div>
            <button
              onClick={() => setRedemptionsPage(prev => Math.min(prev + 1, redemptionsPages))}
              disabled={redemptionsPage === redemptionsPages}
            >
              Next
            </button>
          </PaginationContainer>
        )}
      </TransactionsContainer>

      <TransactionsContainer style={{ marginTop: '1rem' }}>
        <TransactionHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FaDollarSign />
            <h3>Withdrawals</h3>
          </div>
          <HeaderActions>
            <ButtonSmall onClick={() => setShowFilters(v => !v)}>
              Filters
            </ButtonSmall>
          </HeaderActions>
        </TransactionHeader>

        {showFilters && (
          <FilterSection>
            <FilterGroup>
              <FaFilter />
              <label>Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="processed">Processed</option>
                <option value="rejected">Rejected</option>
              </select>
            </FilterGroup>
            <FilterGroup>
              <FaSort />
              <label>Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="requestDate">Request Date</option>
                <option value="amount">Amount</option>
                <option value="status">Status</option>
                <option value="processedDate">Processed Date</option>
              </select>
            </FilterGroup>
            <FilterGroup>
              <label>Order:</label>
              <select
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </FilterGroup>
          </FilterSection>
        )}

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <FaSpinner className="fa-spin" style={{ fontSize: '2rem', color: '#22c55e' }} />
            <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading transactions...</p>
          </div>
        ) : transactions.length === 0 ? (
          <EmptyState>
            <FaHistory />
            <h3>No Payment History</h3>
            <p>
              You haven't made any withdrawal requests yet.
              Visit the Rewards page to redeem your points and request withdrawals.
            </p>
          </EmptyState>
        ) : (
          <TransactionsList $count={transactions.length}>
            {transactions.map((transaction, index) => (
              <TransactionItem key={transaction.id} $status={transaction.status} $delay={index * 40}>
                <TransactionInfo>
                  <div className="transaction-id">
                    Transaction #{transaction.id}
                    {transaction.transactionId && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        (Ref: {transaction.transactionId})
                      </span>
                    )}
                  </div>
                  <div className="transaction-date">
                    <FaCalendarAlt />
                    Requested: {formatDate(transaction.requestDate)}
                    {transaction.processedDate && (
                      <span style={{ marginLeft: '1rem' }}>
                        Processed: {formatDate(transaction.processedDate)}
                      </span>
                    )}
                  </div>
                  {transaction.bankDetails && (
                    <div className="bank-details">
                      Bank: {transaction.bankDetails.bankName} -
                      Account: ****{transaction.bankDetails.accountNumber?.slice(-4)}
                    </div>
                  )}
                  {transaction.rejectionReason && (
                    <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.3rem' }}>
                      Reason: {transaction.rejectionReason}
                    </div>
                  )}
                </TransactionInfo>

                <TransactionAmount>
                  <div className="amount">
                    <FaCoins />
                    {formatPoints(transaction.amount)}
                  </div>
                  <StatusBadge status={transaction.status}>
                    {getStatusIcon(transaction.status)}
                    {transaction.status}
                  </StatusBadge>
                </TransactionAmount>
              </TransactionItem>
            ))}
          </TransactionsList>
        )}

        {totalPages > 1 && (
          <PaginationContainer>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <div className="page-info">
              Page {currentPage} of {totalPages}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </PaginationContainer>
        )}
      </TransactionsContainer>
    </PaymentHistoryContainer>
  );
};

export default PaymentHistory;
